import ProfitSystemLogo from "../components/ProfitSystemLogo";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import {
  ArrowRight, CheckCircle2, TrendingUp, BarChart3, Target,
  Zap, ShieldCheck, Bell, Package, ChevronDown, ChevronUp,
  Store, Layers, AreaChart, RefreshCw
} from "lucide-react";

/* ── Google Fonts ── */
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap');
    :root {
      --red: #C8192C;
      --red-hover: #a81424;
      --dark: #0F0F0F;
      --dark-card: #1A1A1A;
      --warm-white: #F7F6F3;
      --white: #FFFFFF;
      --text-primary: #1A1A1A;
      --text-secondary: #6B7280;
      --border: #E5E4E0;
    }
    body { font-family: 'Inter', sans-serif; }
    .font-syne { font-family: 'Syne', sans-serif; }
    .animate-in { opacity: 0; transform: translateY(28px); transition: opacity 0.65s ease, transform 0.65s ease; }
    .animate-in.visible { opacity: 1; transform: translateY(0); }
    .stagger-1 { transition-delay: 0.05s; }
    .stagger-2 { transition-delay: 0.15s; }
    .stagger-3 { transition-delay: 0.25s; }
    .stagger-4 { transition-delay: 0.35s; }
    .stagger-5 { transition-delay: 0.45s; }
    .stagger-6 { transition-delay: 0.55s; }
    .pain-card:hover { border-color: var(--red); transform: translateY(-3px); }
    .pillar-card:hover { box-shadow: 0 8px 32px rgba(200,25,44,0.10); transform: translateY(-4px); }
    .step-line::after { content:''; position:absolute; left:50%; top:100%; width:1px; height:32px; background: linear-gradient(to bottom, #C8192C, transparent); }
    .funnel-card { background: linear-gradient(135deg, #1A1A1A 0%, #111 100%); border: 1px solid #2a2a2a; }
    .funnel-card:hover { border-color: var(--red); }
  `}</style>
);

/* ── Intersection Observer hook — triggers self + all .animate-in children ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          el.querySelectorAll(".animate-in").forEach(c => c.classList.add("visible"));
          obs.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-[#E5E4E0] rounded-xl overflow-hidden cursor-pointer"
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between px-6 py-5">
        <span className="font-syne font-semibold text-[#1A1A1A] text-base">{q}</span>
        {open
          ? <ChevronUp className="w-5 h-5 text-[#C8192C] shrink-0" />
          : <ChevronDown className="w-5 h-5 text-[#6B7280] shrink-0" />
        }
      </div>
      {open && (
        <div className="px-6 pb-5 text-[#6B7280] text-sm leading-relaxed border-t border-[#E5E4E0] pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  const heroRef    = useReveal();
  const painRef    = useReveal();
  const agujRef    = useReveal();
  const funnelRef  = useReveal();
  const pilaresRef = useReveal();
  const howRef     = useReveal();
  const integRef   = useReveal();
  const faqRef     = useReveal();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <FontLoader />

      {/* ── NAVIGATION ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E5E4E0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <ProfitSystemLogo height={52} /><div className="hidden md:flex items-center gap-8">
            {[["#funcionalidades","Funcionalidades"],["#como-funciona","Cómo funciona"],["#integraciones","Integraciones"]].map(([href,label]) => (
              <a key={href} href={href} className="text-[#6B7280] hover:text-[#C8192C] transition-colors text-sm font-medium">{label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#6B7280] hover:text-[#C8192C] text-sm" data-testid="login-nav-btn">
                Iniciar sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#C8192C] hover:bg-[#a81424] text-white rounded-full px-5 text-sm font-medium transition-all" data-testid="register-nav-btn">
                Prueba gratis 14 días
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="bg-[#0F0F0F] pt-24 pb-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,25,44,0.12),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(200,25,44,0.06),transparent_60%)]" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div ref={heroRef} className="animate-in space-y-7">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase">
              El panel de control que todo eCommerce necesita
            </p>

            <h1 className="font-syne font-black text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight">
              Por fin sabes cuánto{" "}
              <span className="text-[#C8192C]">GANAS</span>,
              <br className="hidden sm:block" />
              {" "}no solo cuánto vendes
            </h1>

            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto leading-relaxed">
              ProfitSystem centraliza tu tienda, publicidad y analítica para darte
              la <strong className="text-white font-medium">rentabilidad real</strong> de tu negocio — en segundos, no en horas de Excel.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Link to="/register">
                <Button size="lg" className="bg-[#C8192C] hover:bg-[#a81424] text-white rounded-full px-8 font-semibold text-base transition-all shadow-lg shadow-red-900/30" data-testid="hero-cta-btn">
                  Empezar prueba gratuita <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#como-funciona">
                <Button size="lg" variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/8 rounded-full px-8 text-base">
                  Ver cómo funciona
                </Button>
              </a>
            </div>

            <p className="text-[#4B5563] text-sm pt-1">14 días gratis · Tarjeta requerida · Sin permanencia</p>
          </div>
        </div>
      </section>

      {/* ── DOLOR AGITADO ── */}
      <section className="py-24 px-6 bg-white" id="funcionalidades">
        <div className="max-w-6xl mx-auto" ref={painRef}>
          <div className="animate-in text-center mb-14">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">El problema</p>
            <h2 className="font-syne font-black text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">
              ¿Te suena alguno de estos?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BarChart3,
                title: "El ROAS que ves no es el ROAS real",
                body: "Tu dashboard de Meta dice 4x. Google dice 5x. Pero cuando miras la cuenta corriente, los números no encajan. La sobre-atribución te está engañando.",
                delay: "stagger-1"
              },
              {
                icon: Package,
                title: "Productos que venden pero no rentabilizan",
                body: "Tienes 300 referencias. Algunas generan margen, otras lo destruyen silenciosamente. Sin los datos correctos, nunca sabrás cuáles son cuáles.",
                delay: "stagger-2"
              },
              {
                icon: AreaChart,
                title: "El Excel mensual que siempre llega tarde",
                body: "En el mejor de los casos, cada mes calculas la rentabilidad en un Excel. Y cada mes te equivocas. En el peor, ni siquiera haces ese control — y eso puede cambiarte el negocio y la vida.",
                delay: "stagger-3"
              }
            ].map(({ icon: Icon, title, body, delay }) => (
              <div
                key={title}
                className={`animate-in ${delay} pain-card border border-[#E5E4E0] rounded-2xl p-7 transition-all duration-300 cursor-default`}
              >
                <div className="w-11 h-11 bg-[#FFF0F1] rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#C8192C]" />
                </div>
                <h3 className="font-syne font-bold text-lg text-[#1A1A1A] mb-3 leading-snug">{title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AGUJEROS ── */}
      <section className="py-24 px-6 bg-[#F7F6F3]">
        <div className="max-w-5xl mx-auto" ref={agujRef}>
          <div className="animate-in">
            <div className="text-center mb-12">
              <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">El error más caro</p>
              <h2 className="font-syne font-black text-3xl md:text-4xl text-[#1A1A1A] tracking-tight max-w-3xl mx-auto leading-snug">
                Meter más dinero en publicidad cuando tu negocio tiene agujeros por donde se escapa la rentabilidad no tiene ningún sentido.
              </h2>
              <p className="text-[#6B7280] text-base mt-5 max-w-2xl mx-auto leading-relaxed">
                Es como abrir el grifo con el desagüe abierto. Antes de escalar, tapa los agujeros.
                Estos son los más comunes — y los más ignorados:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { icon: Target,     text: "Productos con margen real negativo que nadie ha detectado" },
                { icon: RefreshCw,  text: "Clientes que compran una vez y no vuelven (LTV mal gestionado)" },
                { icon: AreaChart,  text: "Costes fijos que no entran en ningún cálculo de rentabilidad" },
                { icon: BarChart3,  text: "Atribución inflada que hace parecer rentables campañas que no lo son" },
                { icon: Package,    text: "Catálogos desactualizados que muestran productos sin stock" },
                { icon: Bell,       text: "Sin alertas: los problemas se detectan semanas después de ocurrir" }
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className={`animate-in stagger-${i+1} flex items-start gap-4 bg-white border border-[#E5E4E0] rounded-xl p-5 transition-all duration-300 hover:border-[#C8192C]/30`}>
                  <div className="w-9 h-9 bg-[#FFF0F1] rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-[#C8192C]" />
                  </div>
                  <p className="text-[#1A1A1A] text-sm leading-snug font-medium">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FUNNEL SIMULATOR ── */}
      <section className="py-24 px-6 bg-[#0F0F0F]">
        <div className="max-w-5xl mx-auto" ref={funnelRef}>
          <div className="animate-in text-center mb-14">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">Optimización de embudo</p>
            <h2 className="font-syne font-black text-4xl md:text-5xl text-white tracking-tight">
              El 1% de conversión que cambia todo
            </h2>
            <p className="text-[#9CA3AF] mt-4 max-w-xl mx-auto text-base leading-relaxed">
              Con la misma inversión en publicidad, la diferencia entre un 1,5% y un 2,5% de conversión no es lineal. Es transformadora.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* LEFT — bad */}
            <div className="funnel-card rounded-2xl p-8 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 rounded-full bg-[#4B5563]" />
                <span className="text-[#9CA3AF] text-sm font-semibold uppercase tracking-wider">Sin optimizar</span>
              </div>
              <div className="space-y-5">
                {[
                  ["Visitas al mes",         "50.000"],
                  ["Tasa de conversión",     "1,5%"],
                  ["Pedidos generados",      "750"],
                  ["Ticket medio",           "€80"],
                  ["Facturación",            "€60.000"],
                  ["Margen neto estimado (30%)", "€18.000"],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between border-b border-white/6 pb-3 last:border-0">
                    <span className="text-[#6B7280] text-sm">{label}</span>
                    <span className="text-white font-semibold">{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — good */}
            <div className="rounded-2xl p-8 border border-[#C8192C]/50 bg-[#1A1A1A] transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C8192C]/6 rounded-bl-full" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 rounded-full bg-[#C8192C]" />
                <span className="text-[#C8192C] text-sm font-semibold uppercase tracking-wider">Con ProfitSystem</span>
              </div>
              <div className="space-y-5">
                {[
                  ["Visitas al mes",            "50.000"],
                  ["Tasa de conversión",         "2,5%"],
                  ["Pedidos generados",           "1.250"],
                  ["Ticket medio",               "€80"],
                  ["Facturación",                "€100.000"],
                  ["Margen neto estimado (30%)", "€30.000"],
                ].map(([label, val], i) => (
                  <div key={label} className="flex items-center justify-between border-b border-white/6 pb-3 last:border-0">
                    <span className="text-[#9CA3AF] text-sm">{label}</span>
                    <span className={`font-bold text-base ${i >= 4 ? "text-[#C8192C]" : "text-white"}`}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-[#C8192C]/10 border border-[#C8192C]/30 rounded-2xl px-8 py-6 text-center">
            <p className="text-white font-syne font-bold text-xl">
              Misma inversión publicitaria.{" "}
              <span className="text-[#C8192C]">€12.000 más de beneficio neto al mes.</span>
            </p>
            <p className="text-[#9CA3AF] text-sm mt-2">
              ProfitSystem analiza tu embudo, detecta dónde pierdes clientes y te muestra exactamente qué mejorar.
            </p>
          </div>
        </div>
      </section>

      {/* ── 3 PILARES ── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto" ref={pilaresRef}>
          <div className="animate-in text-center mb-16">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">Funcionalidades</p>
            <h2 className="font-syne font-black text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">
              Todo el control. Un solo sitio.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                label: "Control financiero total",
                headline: "Beneficio neto real, no solo facturación",
                points: [
                  "Revenue − COGS − costes fijos = beneficio real",
                  "LTV por cliente y tasa de repetición",
                  "Productos más y menos rentables",
                  "Evolución de márgenes en el tiempo",
                  "Resumen anual consolidado"
                ],
                delay: "stagger-1"
              },
              {
                icon: Target,
                label: "Publicidad sin mentiras",
                headline: "El ROAS que importa: el real",
                points: [
                  "Comparativa real vs. reportado por plataformas",
                  "Google Ads + Meta Ads + TikTok + Awin",
                  "Tracking de primera parte (sin cookies 3rd party)",
                  "LTV para calcular cuánto invertir por cliente",
                  "Alertas cuando el ROAS baja del umbral"
                ],
                delay: "stagger-2"
              },
              {
                icon: Layers,
                label: "Operaciones automatizadas",
                headline: "Tu tienda siempre actualizada, sola",
                points: [
                  "Feed optimizer: Google MC + Meta + TikTok",
                  "Sincronización multi-tienda y multi-idioma",
                  "Alertas de stock: rotación, cobertura, agotados",
                  "Notificaciones en tiempo real por Telegram",
                  "Sincronización automática de pedidos"
                ],
                delay: "stagger-3"
              }
            ].map(({ icon: Icon, label, headline, points, delay }) => (
              <div
                key={label}
                className={`animate-in ${delay} pillar-card border border-[#E5E4E0] rounded-2xl p-8 transition-all duration-300`}
              >
                <div className="w-12 h-12 bg-[#FFF0F1] rounded-xl flex items-center justify-center mb-6">
                  <Icon className="w-6 h-6 text-[#C8192C]" />
                </div>
                <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-2">{label}</p>
                <h3 className="font-syne font-bold text-xl text-[#1A1A1A] mb-5 leading-snug">{headline}</h3>
                <ul className="space-y-3">
                  {points.map(p => (
                    <li key={p} className="flex items-start gap-3 text-sm text-[#6B7280]">
                      <CheckCircle2 className="w-4 h-4 text-[#C8192C] shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section id="como-funciona" className="py-24 px-6 bg-[#F7F6F3]">
        <div className="max-w-4xl mx-auto text-center" ref={howRef}>
          <div className="animate-in">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">Proceso</p>
            <h2 className="font-syne font-black text-4xl md:text-5xl text-[#1A1A1A] tracking-tight mb-16">
              Configurado en 5 minutos
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  n: "01",
                  icon: Store,
                  title: "Conecta tu tienda y canales",
                  body: "PrestaShop o Shopify + Google Ads + Meta Ads + GA4. Conectores nativos, sin código."
                },
                {
                  n: "02",
                  icon: RefreshCw,
                  title: "Sincronización automática",
                  body: "ProfitSystem cruza pedidos, costes, publicidad y analítica. Los datos siempre actualizados."
                },
                {
                  n: "03",
                  icon: AreaChart,
                  title: "Decide con datos reales",
                  body: "Rentabilidad real, LTV, ROAS real y alertas. Sin suposiciones, sin Excel, sin sorpresas."
                }
              ].map(({ n, icon: Icon, title, body }) => (
                <div key={n} className="flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 bg-[#0F0F0F] rounded-2xl flex items-center justify-center shadow-lg mb-2">
                    <Icon className="w-7 h-7 text-[#C8192C]" />
                  </div>
                  <span className="font-syne font-black text-4xl text-[#E5E4E0]">{n}</span>
                  <h3 className="font-syne font-bold text-lg text-[#1A1A1A]">{title}</h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed max-w-xs">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── INTEGRACIONES ── */}
      <section id="integraciones" className="py-20 px-6 bg-white border-y border-[#E5E4E0]">
        <div className="max-w-5xl mx-auto" ref={integRef}>
          <div className="animate-in text-center mb-12">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">Integraciones</p>
            <h2 className="font-syne font-bold text-3xl text-[#1A1A1A] tracking-tight">
              Se conecta con todo lo que ya usas
            </h2>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-4">
            {[
              {
                name: "PrestaShop",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#DF0067"/><path d="M60 85c0-13.8 11.2-25 25-25h30c13.8 0 25 11.2 25 25v8c0 13.8-11.2 25-25 25H85c-13.8 0-25-11.2-25-25V85z" fill="white"/><path d="M60 130c0 4.4 3.6 8 8 8h28l-14-28H68c-4.4 0-8 3.6-8 8v12z" fill="white" opacity="0.85"/></svg>
              },
              {
                name: "Shopify",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#96BF48"/><path d="M138 67s-1-1-3-1c-1 0-14-1-14-1s-9-9-10-10c-1-1-3-1-4-1l-5 144 40-10s-4-121-4-121zM111 65s-3-1-6-1c0-3-.5-7-2-10 4 1 7 5 8 11z" fill="white"/><path d="M95 55c-2 5-2 11-2 13H82c1-5 5-12 13-13zM79 68H67s-2 0-2 2l-15 80 54 10-25-92z" fill="white" opacity="0.85"/></svg>
              },
              {
                name: "Google Ads",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#F8F8F8"/><circle cx="68" cy="128" r="22" fill="#FBBC04"/><circle cx="132" cy="128" r="22" fill="#34A853"/><circle cx="100" cy="72" r="22" fill="#4285F4"/></svg>
              },
              {
                name: "Meta Ads",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#1877F2"/><path d="M112 103l3-18H97V74c0-5 2-9 9-9h8V51s-6-1-11-1c-12 0-20 7-20 21v13H71v18h13v43h16v-43h11z" fill="white"/></svg>
              },
              {
                name: "GA4",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#F8F8F8"/><rect x="52" y="120" width="28" height="40" rx="6" fill="#F9AB00"/><rect x="86" y="88" width="28" height="72" rx="6" fill="#E37400" opacity="0.8"/><rect x="120" y="52" width="28" height="108" rx="6" fill="#E37400"/></svg>
              },
              {
                name: "TikTok Ads",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#010101"/><path d="M136 75c-8-1-14-7-17-14v60c0 15-12 27-27 27s-27-12-27-27 12-27 27-27c1 0 3 0 4 1V79c-1 0-3-1-4-1-23 0-42 19-42 42s19 42 42 42 42-19 42-42V89c7 5 16 8 26 8V71c-8 0-15-2-21-6l-3 10z" fill="white"/></svg>
              },
              {
                name: "Awin",
                logo: <svg viewBox="0 0 200 200" className="w-10 h-10"><circle cx="100" cy="100" r="100" fill="#00C4B4"/><text x="100" y="115" textAnchor="middle" fill="white" fontSize="58" fontWeight="800" fontFamily="sans-serif">AW</text></svg>
              },
            ].map(({ name, logo }) => (
              <div key={name} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-[#E5E4E0] hover:border-[#C8192C]/30 transition-colors">
                {logo}
                <span className="text-[#6B7280] text-xs text-center leading-tight">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 px-6 bg-[#F7F6F3]">
        <div className="max-w-3xl mx-auto" ref={faqRef}>
          <div className="animate-in text-center mb-12">
            <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-3">Preguntas frecuentes</p>
            <h2 className="font-syne font-black text-4xl text-[#1A1A1A] tracking-tight">
              Todo lo que necesitas saber
            </h2>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "¿Funciona con PrestaShop y Shopify?",
                a: "Sí. ProfitSystem tiene conectores nativos para PrestaShop y Shopify. La sincronización de pedidos, productos y costes es automática desde el primer día. También es compatible con multi-tienda."
              },
              {
                q: "¿Cuánto tiempo tarda en configurarse?",
                a: "La configuración inicial lleva menos de 5 minutos para conectar tu tienda. Las integraciones con Google Ads, Meta y GA4 siguen el mismo proceso de autorización OAuth. En menos de una hora tienes datos reales en pantalla."
              },
              {
                q: "¿Mis datos están seguros?",
                a: "Toda la comunicación va cifrada (HTTPS/TLS). Los tokens de acceso a plataformas se almacenan cifrados. No vendemos ni compartimos ningún dato con terceros. Cumplimos con GDPR."
              },
              {
                q: "¿Puedo gestionar varias tiendas?",
                a: "Sí. ProfitSystem soporta múltiples tiendas bajo el mismo panel, con posibilidad de vista consolidada o individual por tienda."
              },
              {
                q: "¿Necesito saber de programación para instalarlo?",
                a: "No. Los conectores de PrestaShop y Shopify no requieren código. Para el tracking de primera parte incluimos instrucciones paso a paso para copiar y pegar el snippet en tu tienda."
              }
            ].map(faq => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-28 px-6 bg-[#0F0F0F] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(200,25,44,0.10),transparent_70%)]" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p className="text-[#C8192C] text-xs font-semibold tracking-widest uppercase mb-4">Empieza hoy</p>
          <h2 className="font-syne font-black text-4xl md:text-5xl text-white tracking-tight mb-5 leading-tight">
            Deja de gestionar tu negocio<br />a ciegas
          </h2>
          <p className="text-[#9CA3AF] text-base mb-10 max-w-xl mx-auto leading-relaxed">
            Cada semana sin datos reales es una semana tomando decisiones basadas en suposiciones.
            El momento de cambiar eso es ahora.
          </p>
          <Link to="/register">
            <Button
              size="lg"
              className="bg-[#C8192C] hover:bg-[#a81424] text-white rounded-full px-10 py-6 text-base font-semibold shadow-xl shadow-red-900/30 transition-all"
              data-testid="final-cta-btn"
            >
              Empezar prueba gratuita <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-[#4B5563] text-sm mt-4">14 días gratis · Tarjeta requerida · Sin permanencia</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-6 border-t border-[#E5E4E0] bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <ProfitSystemLogo height={36} />
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm">
            <p className="text-[#9CA3AF]" data-testid="landing-footer-copyright">© 2025 ProfitSystem. Todos los derechos reservados.</p>
            <div className="flex items-center gap-4">
              <Link to="/politica-de-privacidad" className="text-[#9CA3AF] hover:text-[#C8192C] transition-colors" data-testid="footer-privacy-link">Política de privacidad</Link>
              <Link to="/terminos-y-condiciones" className="text-[#9CA3AF] hover:text-[#C8192C] transition-colors" data-testid="footer-terms-link">Términos y condiciones</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
